'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import NavigationLoader from './NavigationLoader';

// Thin top bar that appears the instant an internal link is clicked and completes
// when the new route renders. Without it the old page sits frozen while Next fetches
// the next route's server payload, which reads as "the site is stuck".

const SAFETY_TIMEOUT_MS = 15000; // never leave the bar hanging (aborted/failed nav)
const LOADER_DELAY_MS = 400; // fast navigations only get the bar — no overlay flash
const LOADER_MIN_VISIBLE_MS = 700; // once shown, let the animation read instead of flickering
const LOADER_FADE_MS = 300; // matches the overlay's CSS opacity transition

function isInternalNavigation(event: MouseEvent): string | null {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;

  const anchor = (event.target as Element | null)?.closest('a');
  if (!anchor || !anchor.href) return null;
  if (anchor.target && anchor.target !== '_self') return null;
  if (anchor.hasAttribute('download')) return null;
  // Links whose own click handler loads content in place (e.g. "Load more") — no page change.
  if (anchor.closest('[data-no-progress]')) return null;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;

  const current = new URL(window.location.href);
  // Same page (or just a hash jump) — nothing will load.
  if (url.pathname === current.pathname && url.search === current.search) return null;

  return url.pathname + url.search;
}

export default function NavigationProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const trickleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(false);

  // Overlay loader: mounted while showing or fading out; `loaderVisible` drives the fade.
  const [loaderMounted, setLoaderMounted] = useState(false);
  const [loaderVisible, setLoaderVisible] = useState(false);
  const loaderShownAtRef = useRef<number | null>(null);
  const loaderTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    if (trickleRef.current) clearInterval(trickleRef.current);
    if (safetyRef.current) clearTimeout(safetyRef.current);
    trickleRef.current = null;
    safetyRef.current = null;
    loaderTimersRef.current.forEach(clearTimeout);
    loaderTimersRef.current = [];
  };

  const later = (fn: () => void, ms: number) => {
    loaderTimersRef.current.push(setTimeout(fn, ms));
  };

  const hideLoader = () => {
    const shownAt = loaderShownAtRef.current;
    if (shownAt === null) return;
    const remaining = Math.max(0, LOADER_MIN_VISIBLE_MS - (Date.now() - shownAt));
    later(() => {
      setLoaderVisible(false);
      later(() => {
        loaderShownAtRef.current = null;
        setLoaderMounted(false);
      }, LOADER_FADE_MS);
    }, remaining);
  };

  const finish = () => {
    if (!activeRef.current) return;
    activeRef.current = false;
    clearTimers();
    hideLoader();
    setProgress(100);
    setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 250);
  };

  const start = () => {
    clearTimers();
    activeRef.current = true;
    setVisible(true);
    setProgress(10);
    // Ease toward 90% — fast at first, slowing down, never reaching the end on its own.
    trickleRef.current = setInterval(() => {
      setProgress((p) => (p < 90 ? p + (90 - p) * 0.1 : p));
    }, 200);
    safetyRef.current = setTimeout(finish, SAFETY_TIMEOUT_MS);

    if (loaderShownAtRef.current !== null) {
      setLoaderVisible(true); // already up (or fading) from a previous click — keep it
    } else {
      later(() => {
        loaderShownAtRef.current = Date.now();
        setLoaderMounted(true);
        requestAnimationFrame(() => setLoaderVisible(true));
      }, LOADER_DELAY_MS);
    }
  };

  // Start on any internal link click (covers next/link and plain <a>).
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (isInternalNavigation(event)) start();
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The route changed — the new page has rendered.
  useEffect(() => {
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  useEffect(() => clearTimers, []);

  // Fade the page behind the loader (styled in globals.scss); the new page fades back in.
  useEffect(() => {
    if (loaderVisible) document.body.setAttribute('data-navigating', '');
    else document.body.removeAttribute('data-navigating');
    return () => document.body.removeAttribute('data-navigating');
  }, [loaderVisible]);

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 top-0 z-[9999] h-[3px]"
        style={{ opacity: visible ? 1 : 0, transition: 'opacity 250ms ease' }}
      >
        {/* ink → rose → blush, with a glowing heart riding the tip */}
        <div
          className="relative h-full rounded-r-full bg-gradient-to-r from-[#06233F] via-[#C93D63] to-[#ECAAAA] shadow-[0_0_10px_rgba(201,61,99,0.55)]"
          style={{ width: `${progress}%`, transition: 'width 200ms ease-out' }}
        >
          <svg
            viewBox="0 0 24 24"
            className="absolute -right-[7px] -top-px h-[14px] w-[14px] animate-pulse fill-[#C93D63] drop-shadow-[0_0_6px_rgba(201,61,99,0.8)]"
          >
            <path d="M12 21s-7.5-4.6-9.7-9.3C.6 8.2 2.7 4 6.6 4c2.2 0 3.6 1.2 5.4 3.2C13.8 5.2 15.2 4 17.4 4c3.9 0 6 4.2 4.3 7.7C19.5 16.4 12 21 12 21z" />
          </svg>
        </div>
      </div>
      {loaderMounted && <NavigationLoader visible={loaderVisible} />}
    </>
  );
}
